/* BikiniHouses funnel signals. No pixel, storage, network client, form values or purchase emitter. */
(function (scope) {
  'use strict';
  const SCHEMA_VERSION = '2026-11-03';
  const EVENTS = new Set(['owner_cta_clicked', 'evaluation_started', 'evaluation_completed', 'human_review_requested', 'explore_clicked', 'lodging_consult_clicked', 'interest_registered']);
  const PLACEMENTS = new Set(['hero', 'escalera', 'header', 'menu', 'footer', 'hospedaje', 'descubre', 'coleccion-escalera', 'owner-result', 'travel-card', 'evaluation', 'contact-success', 'future-project', 'other']);
  const INITIAL_FIELDS = new Set(['municipality', 'area', 'access', 'purpose']);
  function pageType(pathname) {
    if (pathname === '/') return 'home';
    if (pathname === '/pages/tengo-un-terreno-en-la-naturaleza') return 'owner';
    if (pathname === '/collections/manuales-y-planos') return 'resources';
    if (pathname === '/pages/hospedate-en-una-aldea-bikinihouses') return 'travel';
    if (pathname === '/pages/historias-de-territorios') return 'stories';
    if (pathname === '/pages/ejemplo-terreno-bikinihouses') return 'future_example';
    return 'other';
  }
  function createFunnel(win, doc) {
    let mounted = false;
    const evaluations = new Map();
    const once = new Set();
    const listeners = [];
    const enqueue = win.queueMicrotask ? win.queueMicrotask.bind(win) : callback => Promise.resolve().then(callback);
    function on(target, type, handler, capture) {
      target.addEventListener(type, handler, capture);
      listeners.push(() => target.removeEventListener(type, handler, capture));
    }
    function debug(name, payload, delivery) {
      try { doc.dispatchEvent(new win.CustomEvent('bh53:funnel:debug', {detail: {name: 'bh53:' + name, payload, delivery}})); } catch (_) { /* Analytics must never interrupt the page. */ }
    }
    function emit(name, meta) {
      if (!EVENTS.has(name)) return false;
      const info = meta || {};
      const payload = {schema_version: SCHEMA_VERSION, page: pageType(win.location.pathname), placement: PLACEMENTS.has(info.placement) ? info.placement : 'other'};
      if (name === 'evaluation_completed') payload.evaluation_depth = info.depth === 'full' ? 'full' : 'initial';
      if (name === 'human_review_requested') payload.confirmation = 'shopify_contact_success';
      if (name === 'interest_registered') payload.confirmation = 'backend_vote_confirmed';
      if (doc.documentElement.dataset.bh53TrackingEnabled !== 'true') { debug(name, payload, 'disabled_preview'); return false; }
      if (win.Shopify && win.Shopify.designMode) { debug(name, payload, 'design_mode'); return false; }
      const privacy = win.Shopify && win.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') { debug(name, payload, 'privacy_unavailable'); return false; }
      let allowed = false;
      try { allowed = privacy.analyticsProcessingAllowed() === true; } catch (_) { /* Fail closed. */ }
      if (!allowed) { debug(name, payload, 'consent_not_allowed'); return false; }
      const analytics = win.Shopify && win.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') { debug(name, payload, 'publisher_unavailable'); return false; }
      try {
        const result = analytics.publish('bh53:' + name, payload);
        debug(name, payload, result === false ? 'publish_rejected' : 'publish_called');
        if (result && typeof result.catch === 'function') result.catch(() => debug(name, payload, 'publish_failed'));
        return result !== false;
      } catch (_) { debug(name, payload, 'publish_failed'); return false; }
    }
    function isVisible(element) {
      return Boolean(element && !element.hidden && !element.closest('[hidden]'));
    }
    function initialValid(root) {
      return Array.from(INITIAL_FIELDS).every(name => {
        const field = root.querySelector('[data-bh-field="' + name + '"]');
        return Boolean(field && typeof field.value === 'string' && field.value.trim() && (!field.checkValidity || field.checkValidity()));
      });
    }
    function initialResult(root) {
      const explicit = root.querySelector('[data-bh53-evaluation-result="initial"]');
      if (explicit) return explicit;
      const title = root.querySelector('[data-bh-first-title]');
      return title && title.closest('[data-bh-step]');
    }
    function inspectEvaluation(root, state) {
      if (!state.started || state.completed || !initialValid(root)) return;
      const full = root.querySelector('[data-bh53-evaluation-result="full"], [data-bh-result]');
      const initial = initialResult(root);
      if (!isVisible(initial) && !isVisible(full)) return;
      state.completed = true;
      emit('evaluation_completed', {placement: 'evaluation', depth: isVisible(initial) ? 'initial' : 'full'});
    }
    function scheduleEvaluation(root, state) {
      if (state.scheduled) return;
      state.scheduled = true;
      enqueue(() => { state.scheduled = false; if (root.isConnected !== false) inspectEvaluation(root, state); });
    }
    function bindEvaluations() {
      doc.querySelectorAll('[data-bh53-evaluation], #bh-step1').forEach(root => {
        if (evaluations.has(root)) return;
        const state = {started: false, completed: false, scheduled: false, observer: null};
        if (typeof win.MutationObserver === 'function') {
          state.observer = new win.MutationObserver(() => scheduleEvaluation(root, state));
          state.observer.observe(root, {subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'style', 'class']});
        }
        evaluations.set(root, state);
      });
    }
    function handleAnswer(event) {
      if (event.isTrusted !== true || !event.target || !event.target.closest) return;
      const field = event.target.closest('[data-bh-field]');
      if (!field || !INITIAL_FIELDS.has(field.dataset.bhField)) return;
      const root = field.closest('[data-bh53-evaluation], #bh-step1');
      const state = root && evaluations.get(root);
      if (!state) return;
      // Editing an initial answer invalidates the prior result; optional detail questions do not create another completion.
      if (state.completed) { state.completed = false; state.started = false; }
      if (!state.started && typeof field.value === 'string' && field.value.trim()) {
        state.started = true;
        emit('evaluation_started', {placement: 'evaluation'});
      }
      scheduleEvaluation(root, state);
    }
    function handleClick(event) {
      if (event.isTrusted !== true || !event.target || !event.target.closest) return;
      const action = event.target.closest('a, button');
      if (!action || action.disabled || action.getAttribute('aria-disabled') === 'true') return;
      const intent = action.dataset.bhIntent;
      const placement = action.dataset.bhPlacement;
      if (intent === 'terreno') emit('owner_cta_clicked', {placement});
      else if (intent === 'viajero' || intent === 'explorar') emit('explore_clicked', {placement});
      else if ((intent === 'lodging' || intent === 'reserva') && action.dataset.bhOperating === 'true') emit('lodging_consult_clicked', {placement});
    }
    function handleConfirmation(event) {
      const detail = event.detail;
      if (!detail || detail.confirmed !== true) return;
      const name = typeof detail.name === 'string' ? detail.name.replace(/^bh53:/, '') : '';
      if (name === 'human_review_requested' && detail.source === 'shopify_contact_success') {
        if (once.has(name)) return;
        once.add(name);
        emit(name, {placement: 'contact-success'});
      } else if (name === 'interest_registered' && detail.source === 'backend_vote_confirmed') {
        if (once.has(name)) return;
        once.add(name);
        emit(name, {placement: 'future-project'});
      }
    }
    function readServerConfirmation() {
      // This marker must be rendered solely inside Liquid form.posted_successfully?. Never place it on a submit button.
      if (doc.querySelector('[data-bh53-review-confirmed="shopify_contact_success"]')) handleConfirmation({detail: {name: 'human_review_requested', confirmed: true, source: 'shopify_contact_success'}});
    }
    function start() {
      if (mounted) return;
      mounted = true;
      bindEvaluations();
      on(doc, 'click', handleClick, true);
      on(doc, 'input', handleAnswer, true);
      on(doc, 'change', handleAnswer, true);
      on(doc, 'bh53:funnel', handleConfirmation);
      on(doc, 'reset', event => {
        for (const [root, state] of evaluations) if (root.contains(event.target)) { state.started = false; state.completed = false; }
      }, true);
      on(doc, 'shopify:section:load', () => { bindEvaluations(); readServerConfirmation(); });
      readServerConfirmation();
    }
    function destroy() {
      listeners.splice(0).forEach(remove => remove());
      evaluations.forEach(state => { if (state.observer) state.observer.disconnect(); });
      evaluations.clear();
      mounted = false;
    }
    return {start, destroy};
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {createFunnel, pageType};
  if (!scope || !scope.document || scope.__bh53FunnelMounted) return;
  scope.__bh53FunnelMounted = true;
  const controller = createFunnel(scope, scope.document);
  if (scope.document.readyState === 'loading') scope.document.addEventListener('DOMContentLoaded', controller.start, {once: true});
  else controller.start();
})(typeof window !== 'undefined' ? window : null);
